import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IMentorTopic, defaultValue } from 'app/shared/model/mentor-topic.model';

const initialState: EntityState<IMentorTopic> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/mentor-topics';

// Actions

export const getEntities = createAsyncThunk('mentorTopic/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<IMentorTopic[]>(requestUrl);
});

export const getEntity = createAsyncThunk(
  'mentorTopic/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IMentorTopic>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getEntityMentorTopic = createAsyncThunk(
  'mentorTopic/fetch_entity_mentortopic',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/?userId.equals=${id}`;
    return axios.get<IMentorTopic>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);
export const createEntity = createAsyncThunk(
  'mentorTopic/create_entity',
  async (entity: IMentorTopic, thunkAPI) => {
    const result = await axios.post<IMentorTopic>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const createEntityMentorTopic = createAsyncThunk(
  'mentorTopic/create_entity_mentor_topic',
  async (entity: IMentorTopic, thunkAPI) => {
    const result = await axios.post<IMentorTopic>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'mentorTopic/update_entity',
  async (entity: IMentorTopic, thunkAPI) => {
    const result = await axios.put<IMentorTopic>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntityMentorTopics = createAsyncThunk(
  'mentorTopic/update_entity_mentor_topics',
  async (entity: IMentorTopic, thunkAPI) => {
    const result = await axios.put<IMentorTopic>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'mentorTopic/partial_update_entity',
  async (entity: IMentorTopic, thunkAPI) => {
    const result = await axios.patch<IMentorTopic>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'mentorTopic/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IMentorTopic>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntityMentorTopic = createAsyncThunk(
  'mentorTopic/delete_entity_mentortopic',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IMentorTopic>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const MentorTopicSlice = createEntitySlice({
  name: 'mentorTopic',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addCase(deleteEntityMentorTopic.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addCase(getEntityMentorTopic.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data,
          totalItems: parseInt(headers['x-total-count'], 10),
        };
      })
      .addMatcher(
        isFulfilled(createEntity, updateEntity, updateEntityMentorTopics, partialUpdateEntity, createEntityMentorTopic),
        (state, action) => {
          state.updating = false;
          state.loading = false;
          state.updateSuccess = true;
          state.entity = action.payload.data;
        }
      )
      .addMatcher(isPending(getEntities, getEntity, getEntityMentorTopic), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(
        isPending(
          createEntity,
          updateEntity,
          updateEntityMentorTopics,
          partialUpdateEntity,
          deleteEntity,
          createEntityMentorTopic,
          deleteEntityMentorTopic
        ),
        state => {
          state.errorMessage = null;
          state.updateSuccess = false;
          state.updating = true;
        }
      );
  },
});

export const { reset } = MentorTopicSlice.actions;

// Reducer
export default MentorTopicSlice.reducer;
